import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Factory,
  ArrowLeft,
  Package,
  Hand,
  Activity,
  ClipboardList,
  BarChart2,
  Wallet,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import AppliedFilters from '@/components/AppliedFilters';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import { getInventarioPlanta } from '../deposito/services/deposito.services';

const formatDateTimeCO = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('es-CO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
  } catch (_) {
    return '';
  }
};

// Tooltip personalizado para la gráfica
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold mb-1 text-foreground">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: <span className="font-medium">{entry.value.toLocaleString('es-CO')}</span>
        </p>
      ))}
    </div>
  );
};

// Tabla de resumen reutilizable (por prenda, por centro de costo, etc.)
const ResumenTable = ({ icon: Icon, title, description, columns, rows, rowKey, emptyMessage, exportButton }) => (
  <Card className="border-2 border-primary/20 rounded-2xl overflow-hidden">
    <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between bg-muted/20">
      <div>
        <CardTitle className="text-xl font-semibold flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" /> {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      {exportButton}
    </CardHeader>
    <CardContent className="pt-6">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto border border-border rounded-xl custom-scrollbar">
          <table className="w-full table-auto text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left">
                {columns.map((col) => (
                  <th key={col.key} className="px-4 py-3 font-semibold text-foreground whitespace-nowrap">{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row[rowKey] ?? idx}
                  className="border-t border-border odd:bg-transparent even:bg-muted/20 hover:bg-primary/5 transition-colors"
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-2 ${col.className || ''}`}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

const SUMMARY_CARD_BASE = 'rounded-2xl text-white shadow-lg p-6 flex flex-col gap-4 transition-transform hover:-translate-y-0.5';
const ITEMS_PER_PAGE = 10;
const INITIAL_TOTALES = { totalRecibidas: 0, totalEntregadas: 0, totalPendientes: 0 };

const buildInitialFilters = () => {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return {
    createdAt: {
      $gte: monthStart.toISOString(),
      $lte: monthEnd.toISOString(),
    },
  };
};

const PlantaInventario = () => {
  const { idPlanta } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [planta, setPlanta] = useState(null);
  const [totales, setTotales] = useState(INITIAL_TOTALES);
  const [isLoading, setIsLoading] = useState(true);

  const [filters, setFilters] = useState(buildInitialFilters);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [resumenPorPrenda, setResumenPorPrenda] = useState([]);
  const [resumenPorCentroCosto, setResumenPorCentroCosto] = useState([]);
  const [centrosCostoOptions, setCentrosCostoOptions] = useState([]);

  const formatNumber = (value) => Number(value || 0).toLocaleString('es-CO');

  // Cargar centros de costo disponibles para el filtro
  useEffect(() => {
    const loadCentrosCosto = async () => {
      try {
        const { centrosCostoService } = await import('@/pages/parametrizacion/centrosCosto/utils/centrosCostoService');
        const response = await centrosCostoService.getAll();
        const centrosCosto = response?.data || [];
        setCentrosCostoOptions(centrosCosto.map((cc) => ({
          value: cc._id || cc.id,
          label: cc.nombre,
        })));
      } catch (error) {
        console.error('Error cargando centros de costo:', error);
      }
    };
    loadCentrosCosto();
  }, []);

  // Carga inicial: solo datos de la planta
  useEffect(() => {
    if (!idPlanta) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { plantasService } = await import('@/pages/parametrizacion/plantas/utils/plantasService');
        const plantaResponse = await plantasService.getById(idPlanta);
        setPlanta(plantaResponse?.data || null);
      } catch (error) {
        console.error('Error cargando datos de planta:', error);
        toast({
          title: 'Error',
          description: 'No se pudo cargar la información de la planta.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [idPlanta, toast]);

  // Carga paginada: trae tabla + totales (respetan filtros) + resumen por prenda
  const loadGarments = useCallback(async (page, appliedFilters) => {
    if (!idPlanta) return;
    setIsTableLoading(true);
    try {
      const response = await getInventarioPlanta({
        idPlanta,
        page,
        limit: ITEMS_PER_PAGE,
        filters: appliedFilters,
      });

      setTableData(response?.data || []);
      setTotalItems(response?.totalRecords || 0);
      setResumenPorPrenda(response?.extra?.resumen || []);
      setResumenPorCentroCosto(response?.extra?.resumenPorCentroCosto || []);
      setTotales(response?.extra?.totales || INITIAL_TOTALES);
    } catch (error) {
      console.error('Error al cargar la tabla de prendas:', error);
      toast({
        title: 'Error',
        description: 'Ocurrió un problema cargando las prendas de la tabla.',
        variant: 'destructive',
      });
    } finally {
      setIsTableLoading(false);
    }
  }, [idPlanta, toast]);

  useEffect(() => {
    loadGarments(currentPage, filters);
  }, [loadGarments, currentPage, filters]);

  const FILTER_FIELDS = useMemo(() => ([
    {
      id: 'estado',
      label: 'Estado de recolección',
      type: 'select',
      options: [
        { value: 'recibido', label: 'Recibido' },
        { value: 'pendiente_verificacion', label: 'Pendiente por verificar' },
        { value: 'verificado', label: 'Verificado' },
        { value: 'entregado', label: 'Entregado' },
        { value: 'rechazado', label: 'Rechazado' },
      ],
    },
    { id: 'consecutivo', label: 'Consecutivo recolección', type: 'text' },
    { id: 'empleado.cedula', label: 'Cédula empleado', type: 'text' },
    {
      id: 'empleado.centroCosto.id',
      label: 'Centro de Costos',
      type: 'select',
      options: centrosCostoOptions,
    },
    { id: 'createdAt', label: 'Fecha de recolección', type: 'daterange' },
  ]), [centrosCostoOptions]);

  const tableColumns = useMemo(() => ([
    {
      id: 'recoleccionConsecutivo',
      label: 'Recolección',
      render: (row) => row.recoleccionConsecutivo || 'Sin consecutivo',
    },
    { id: 'nombre', label: 'Prenda', render: (row) => row.nombre || 'N/A' },
    { id: 'cantidad', label: 'Cantidad', type: 'number' },
    { id: 'empleado', label: 'Empleado', render: (row) => row.empleado?.nombre || 'N/A' },
    { id: 'empleadoCedula', label: 'Cédula', render: (row) => row.empleado?.cedula || 'N/A' },
    { id: 'plantaNombre', label: 'Planta', render: (row) => row.plantaNombre || 'N/A' },
    { id: 'centroCosto', label: 'Centro de Costos', render: (row) => row.empleado?.centroCosto?.nombre || 'N/A' },
    {
      id: 'estadoRecoleccion',
      label: 'Estado recolección',
      type: 'status',
      render: (row) => row.estadoRecoleccion || 'Sin estado',
    },
    {
      id: 'recoleccionFecha',
      label: 'Fecha',
      type: 'date',
      render: (row) => row.recoleccionFecha,
    },
    {
      id: 'opciones',
      label: 'Opciones',
      type: 'opciones',
      actions: [{ key: 'preview', icon: 'Eye', tooltip: 'Generar vista previa', className: 'text-blue-600 hover:bg-blue-50' }],
    },
  ]), []);

  const handlePageChange = (page) => setCurrentPage(page);

  // El pipeline de inventario hace $lookup + $unwind por cada prenda de la planta;
  // sin un rango de fechas escanearía todo el histórico, lo cual es lento. Por eso
  // el filtro de fecha nunca se deja vacío: si se quita o se limpia, se restablece
  // al mes actual en vez de desaparecer.
  const hasDateRange = (candidateFilters) => Boolean(
    candidateFilters?.createdAt?.$gte || candidateFilters?.createdAt?.$lte
  );

  const ensureDateFilter = (candidateFilters) => {
    if (hasDateRange(candidateFilters)) return candidateFilters;
    return { ...candidateFilters, createdAt: buildInitialFilters().createdAt };
  };

  const handleApplyFilters = (applied) => {
    if (!hasDateRange(applied)) {
      toast({
        title: 'Rango de fechas requerido',
        description: 'Para mantener la búsqueda rápida, el inventario siempre se filtra por un rango de fechas. Se restableció al mes actual.',
      });
    }
    setFilters(ensureDateFilter(applied));
    setCurrentPage(1);
  };

  const handleExport = async () => {
    if (!totalItems) {
      toast({
        title: 'Sin datos para exportar',
        description: 'Ajusta los filtros para obtener registros y volver a exportar.',
        variant: 'destructive',
      });
      return [];
    }
    const response = await getInventarioPlanta({ idPlanta, page: 1, limit: 99999, filters });
    return response?.data || [];
  };

  const excelColumns = useMemo(() => ([
    { key: 'recoleccionConsecutivo', header: 'Recolección' },
    { key: 'nombre', header: 'Prenda' },
    { key: 'cantidad', header: 'Cantidad' },
    { key: 'empleado.nombre', header: 'Empleado' },
    { key: 'empleado.cedula', header: 'Cédula' },
    { key: 'plantaNombre', header: 'Planta' },
    { key: 'empleado.centroCosto.nombre', header: 'Centro de Costos' },
    { key: 'estadoRecoleccion', header: 'Estado', transform: (v) => v || 'Sin estado' },
    { key: 'recoleccionFecha', header: 'Fecha recolección', transform: (v) => formatDateTimeCO(v) },
    { key: 'createdAt', header: 'Creado en', transform: (v) => formatDateTimeCO(v) },
    { key: 'updatedAt', header: 'Actualizado en', transform: (v) => formatDateTimeCO(v) },
  ]), []);

  const excelSummaryColumns = useMemo(() => ([
    { key: 'prenda', header: 'Prenda' },
    { key: 'totalRecibidas', header: 'Total Recibidas' },
    { key: 'totalEntregadas', header: 'Total Entregadas' },
    { key: 'totalPendientes', header: 'Pendientes' },
  ]), []);

  const handleSummaryExport = async () => {
    if (!resumenPorPrenda.length) {
      toast({ title: 'Sin datos para exportar', description: 'No hay información agregada de prendas.', variant: 'destructive' });
      return { data: [] };
    }
    return { data: resumenPorPrenda };
  };

  const excelSummaryColumnsCentroCosto = useMemo(() => ([
    { key: 'centroCosto', header: 'Centro de Costos' },
    { key: 'totalRecibidas', header: 'Total Recibidas' },
    { key: 'totalEntregadas', header: 'Total Entregadas' },
    { key: 'totalPendientes', header: 'Pendientes' },
  ]), []);

  const handleSummaryExportCentroCosto = async () => {
    if (!resumenPorCentroCosto.length) {
      toast({ title: 'Sin datos para exportar', description: 'No hay información agregada por centro de costos.', variant: 'destructive' });
      return { data: [] };
    }
    return { data: resumenPorCentroCosto };
  };

  const renderSummaryCard = (title, value, Icon, gradient) => (
    <Card className={`${SUMMARY_CARD_BASE} ${gradient}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide opacity-80">{title}</p>
          <h3 className="text-3xl font-bold mt-2">{formatNumber(value)}</h3>
        </div>
        <div className="bg-white/20 p-3 rounded-xl">
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </Card>
  );

  // Limita la gráfica a las 15 prendas con más recibidas para legibilidad
  const chartData = useMemo(() => resumenPorPrenda.slice(0, 15), [resumenPorPrenda]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto p-4 md:p-6 lg:p-8"
      >
        <Card className="border-2 border-primary/20 rounded-2xl mb-6">
          <CardContent className="p-4 md:p-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 p-4 rounded-lg">
                <Factory className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Gestión de prendas</h1>
                {planta && (
                  <p className="text-muted-foreground">
                    {planta.nombre} • {planta.ciudad || 'Ciudad no disponible'}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(`/plantas/${idPlanta}`)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Volver
            </Button>
          </CardContent>
        </Card>

        {isLoading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p className="text-muted-foreground">Cargando inventario...</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">

            {/* Tarjetas de totales — se actualizan con los filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {renderSummaryCard('Prendas recibidas', totales.totalRecibidas, Package, 'bg-gradient-to-br from-[#024281] to-[#024281]')}
              {renderSummaryCard('Prendas entregadas', totales.totalEntregadas, Hand, 'bg-gradient-to-br from-[#7ac143] to-[#7ac143]')}
              {renderSummaryCard('Pendientes por entregar', totales.totalPendientes, Activity, 'bg-gradient-to-br from-[#dc2626] to-[#dc2626]')}
            </div>

            {/* Tabla de inventario paginada */}
            <Card className="border-2 border-primary/20 rounded-2xl overflow-hidden">
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between bg-muted/20">
                <div>
                  <CardTitle className="text-xl font-semibold flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-primary" /> Inventario de prendas recibidas
                  </CardTitle>
                  <CardDescription>
                    Consulta, filtra y exporta las prendas registradas en las recolecciones de la planta.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => setIsFilterDrawerOpen(true)}>
                    Filtros
                  </Button>
                  <ExportExcelButton
                    service={handleExport}
                    filters={filters}
                    columns={excelColumns}
                    dataMapper={(item) => item}
                    fileName={`inventario_prendas_${new Date().toISOString().slice(0, 10)}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <AppliedFilters
                  filters={filters}
                  fields={FILTER_FIELDS}
                  onRemoveFilter={(key) => {
                    if (key === 'createdAt') {
                      setFilters((prev) => ({ ...prev, createdAt: buildInitialFilters().createdAt }));
                      toast({
                        title: 'Rango de fechas requerido',
                        description: 'Para mantener la búsqueda rápida, el inventario siempre se filtra por un rango de fechas. Se restableció al mes actual.',
                      });
                    } else {
                      setFilters((prev) => { const u = { ...prev }; delete u[key]; return u; });
                    }
                    setCurrentPage(1);
                  }}
                />
                <DataTable
                  columns={tableColumns}
                  data={tableData}
                  isLoading={isTableLoading}
                  totalRecords={totalItems}
                  page={currentPage}
                  limit={ITEMS_PER_PAGE}
                  onPageChange={handlePageChange}
                  onLimitChange={() => { }}
                />
              </CardContent>
            </Card>

            {/* Gráfica por prenda */}
            <Card className="border-2 border-primary/20 rounded-2xl overflow-hidden">
              <CardHeader className="bg-muted/20">
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <BarChart2 className="h-5 w-5 text-primary" /> Comportamiento por prenda
                </CardTitle>
                <CardDescription>
                  Comparativo de prendas recibidas, entregadas y pendientes según los filtros aplicados
                  {chartData.length < resumenPorPrenda.length && ` (mostrando top ${chartData.length} de ${resumenPorPrenda.length})`}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay datos para mostrar en la gráfica.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="prenda"
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        angle={-35}
                        textAnchor="end"
                        interval={0}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => v.toLocaleString('es-CO')}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                        formatter={(value) => ({ recibidas: 'Recibidas', entregadas: 'Entregadas', pendientes: 'Pendientes' }[value] ?? value)}
                      />
                      <Bar dataKey="totalRecibidas" name="recibidas" fill="#024281" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="totalEntregadas" name="entregadas" fill="#7ac143" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="totalPendientes" name="pendientes" fill="#dc2626" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Resumen tabular por prenda */}
            <ResumenTable
              icon={Package}
              title="Resumen por prenda"
              description="Totales comparativos de prendas recibidas y entregadas según los filtros aplicados."
              emptyMessage="No hay datos de prendas para mostrar en el resumen."
              rows={resumenPorPrenda}
              rowKey="prenda"
              exportButton={(
                <ExportExcelButton
                  service={handleSummaryExport}
                  columns={excelSummaryColumns}
                  fileName={`resumen_prendas_${new Date().toISOString().slice(0, 10)}`}
                />
              )}
              columns={[
                { key: 'prenda', header: 'Prenda', className: 'font-medium text-foreground' },
                { key: 'totalRecibidas', header: 'Recibidas', render: (row) => formatNumber(row.totalRecibidas) },
                { key: 'totalEntregadas', header: 'Entregadas', render: (row) => formatNumber(row.totalEntregadas) },
                { key: 'totalPendientes', header: 'Pendientes', render: (row) => formatNumber(row.totalPendientes) },
              ]}
            />

            {/* Resumen tabular por centro de costos */}
            <ResumenTable
              icon={Wallet}
              title="Resumen por centro de costos"
              description="Totales comparativos de prendas recibidas, entregadas y pendientes agrupados por centro de costos."
              emptyMessage="No hay datos de centros de costos para mostrar en el resumen."
              rows={resumenPorCentroCosto}
              rowKey="centroCostoId"
              exportButton={(
                <ExportExcelButton
                  service={handleSummaryExportCentroCosto}
                  columns={excelSummaryColumnsCentroCosto}
                  fileName={`resumen_centros_costo_${new Date().toISOString().slice(0, 10)}`}
                />
              )}
              columns={[
                { key: 'centroCosto', header: 'Centro de Costos', className: 'font-medium text-foreground' },
                { key: 'totalRecibidas', header: 'Recibidas', render: (row) => formatNumber(row.totalRecibidas) },
                { key: 'totalEntregadas', header: 'Entregadas', render: (row) => formatNumber(row.totalEntregadas) },
                { key: 'totalPendientes', header: 'Pendientes', render: (row) => formatNumber(row.totalPendientes) },
              ]}
            />

          </div>
        )}
      </motion.div>

      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        fields={FILTER_FIELDS}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
    </>
  );
};

export default PlantaInventario;
